import type { BatchDeclineItem, BatchDeclineSummary } from '../model/batch-decline';
import type { BatchDeclineRequest, BatchDeclineResult } from '../services/proposal.service';
import { DECLINABLE_STATUSES, type SiteDetail } from './bulk-eligibility.util';
import { buildProductTitle } from './list-grouping.util';
import { productStatusLabel } from './product-status.util';

export const BATCH_DECLINE_FALLBACK = "We couldn't decline these products due to an error. Please try again later or contact support if the issue persists.";
// The server answered with a result the app could not read or show; the
// decline may have worked, so this must not say it failed.
export const BATCH_RESULT_NOT_SHOWN_MESSAGE = "We couldn't show the result of this decline. Reload the page to see each product's current status.";
export const RELOAD_FAILED_MESSAGE = "We couldn't refresh the list. Reload the page to see each product's current status.";
export const GATEWAY_CONFIRMING_MESSAGE = "We're confirming what was declined. These products' buttons will unlock when that finishes.";
export const GATEWAY_RELOAD_PENDING_MESSAGE = "We couldn't refresh the list yet. Keep this page open; these products' buttons will unlock when we've confirmed what was declined.";
export const GATEWAY_RELOAD_WAITS_MS = [10000, 20000, 30000, 60000] as const;
export const GATEWAY_POLL_DEADLINE_MS = 120000;
export const BATCH_RELOAD_TIMEOUT_MS = 30000;
export const BATCH_RELOAD_MAX_ATTEMPTS = 3;
// Above the companion backend plan's ~105 s worst case for a large batch, so a
// slow but healthy request is not cut off. A timeout is an unknown outcome and
// takes the gateway path.
export const BATCH_DECLINE_TIMEOUT_MS = 120000;
// connect-service refuses a batch of more than 500 ids and declines nothing,
// so Decline All is disabled above it rather than sent.
export const BATCH_DECLINE_MAX_PRODUCTS = 500;
export const DECLINE_ALL_TOO_MANY_REASON = `Decline All works on up to ${BATCH_DECLINE_MAX_PRODUCTS} products at a time. Use the site buttons or decline products individually.`;

export function tooManyToDecline(rows: readonly unknown[]): boolean {
  return rows.length > BATCH_DECLINE_MAX_PRODUCTS;
}

export function BATCH_DECLINE_IN_PROGRESS(count: number): string {
  return `Declining ${count} ${count === 1 ? 'product' : 'products'}…`;
}

export function isGatewayError(status: number): boolean {
  return status === 0 || status === 502 || status === 503 || status === 504;
}

/**
 * Whether any sent product may still be declining. A sent product missing
 * from the reloaded cache counts as settled only when the caller knows it was
 * deleted (`missingMeansDeleted`): its project is listed and no search hides rows.
 */
export function pendingDeclines(
  sentIds: readonly string[],
  statuses: ReadonlyMap<string, string | null>,
  missingMeansDeleted: boolean
): boolean {
  return sentIds.some((id) => {
    if (!statuses.has(id)) {
      return !missingMeansDeleted;
    }
    return DECLINABLE_STATUSES.has(statuses.get(id) ?? '');
  });
}

/** How Decline All and its result dialog name one product row. */
export function batchDeclineItem(
  row: SiteDetail,
  statusLabel: string = productStatusLabel(row.proposal?.status ?? '')
): BatchDeclineItem {
  return {
    siteNumber: row.site?.siteNumber ?? null,
    title: buildProductTitle(row.site),
    statusLabel,
  };
}

/** Reconcile only the sent products; reload policy belongs to BatchDeclineService. */
export function applyBatchDeclineResult(
  sentRows: readonly SiteDetail[],
  result: BatchDeclineResult,
  message: string,
  declineFields: Omit<BatchDeclineRequest, 'proposalIds'>
): { changedRows: SiteDetail[]; summary: BatchDeclineSummary } {
  const byId = new Map(result.products.map((product) => [product.proposalId, product]));
  const changedRows: SiteDetail[] = [];
  const summary: BatchDeclineSummary = {
    declined: result.declined,
    total: result.total,
    message,
    failed: [],
    skipped: [],
  };

  for (const row of sentRows) {
    const product = byId.get(row.proposal?.id);
    if (product?.outcome === 'DECLINED') {
      changedRows.push({
        ...row,
        proposal: {
          ...row.proposal,
          status: product.status ?? 'DECLINED',
          declineReason: declineFields.declineReason,
          declineOtherReason: declineFields.declineOtherReason,
          declineComments: declineFields.declineComments,
        },
      });
    } else if (product?.outcome === 'SKIPPED') {
      const label = product.status == null
        ? 'No longer available'
        : productStatusLabel(product.status);
      summary.skipped.push(batchDeclineItem(row, label));
    } else {
      // Missing and future outcomes are uncertain, just like a reported failure.
      summary.failed.push(batchDeclineItem(row));
    }
  }

  return { changedRows, summary };
}
